// SourceArtifactPanel — the detail card for the SOURCE ARTIFACT a citation points to (DF-2).
// Presentational: given a resolved SourceLite (+ optional chunk count), renders filename, zone,
// PII/quarantine posture, and the content/size/uploader/date line. Apps use it as the "open source"
// target of a CitationPopover.
import { FileText, ShieldAlert } from "lucide-react";

import type { SourceLite } from "./types";

export interface SourceArtifactPanelProps {
  source: SourceLite;
  /** How many of this source's chunks appear in the current context graph (optional). */
  chunkCount?: number;
}

function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
function shortDate(iso: string): string {
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
}

export function SourceArtifactPanel({ source, chunkCount }: SourceArtifactPanelProps): React.ReactElement {
  return (
    <div className="rounded-[2px] border border-[var(--color-border-subtle)] bg-[var(--color-surface-base)] p-3" data-zone={source.zone}>
      <div className="flex items-start gap-2">
        <FileText size={16} className="mt-0.5 shrink-0 text-[var(--color-text-secondary)]" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-[var(--color-text-primary)]" title={source.filename}>
              {source.filename}
            </span>
            <span className="rounded-[2px] bg-[var(--color-surface-muted)] px-1 py-px text-[10px] text-[var(--color-text-secondary)]">
              {source.zone}
            </span>
            {source.pii_findings > 0 && (
              <span className="rounded-[2px] bg-[var(--color-warning-500)]/15 px-1 py-px text-[10px] text-[var(--color-warning-500)]">
                PII {source.pii_findings}
              </span>
            )}
            {source.quarantined && (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-[var(--color-danger-500)]">
                <ShieldAlert size={11} aria-hidden /> quarantined
              </span>
            )}
          </div>
          <p className="mt-1 truncate text-xs text-[var(--color-text-secondary)]">
            {source.content_type} · {humanBytes(source.byte_size)} · {source.uploaded_by} · {shortDate(source.created_at)}
          </p>
          {chunkCount !== undefined && (
            <p className="mt-1 text-xs text-[var(--color-text-tertiary)]">
              {chunkCount} chunk{chunkCount === 1 ? "" : "s"} in this context
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
